import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FolderOpen, Upload, Link as LinkIcon, Download, Trash2, FileText, Presentation, Video, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

const KIND_LABEL = { slide: "Slide", pdf: "PDF", video: "Video", link: "Link", altro: "Altro" };
const KIND_ICON = {
  slide: Presentation, pdf: FileText, video: Video, link: LinkIcon, altro: FileText,
};

export default function CourseMaterials({ courseId, canManage = false }) {
  const qc = useQueryClient();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [delMaterial, setDelMaterial] = useState(null);
  const fileRef = useRef(null);
  const [uploadForm, setUploadForm] = useState({ title: "", kind: "slide", description: "" });
  const [linkForm, setLinkForm] = useState({ title: "", url: "", description: "" });
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const { data: materials = [], isLoading } = useQuery({
    queryKey: ["course-materials", courseId],
    queryFn: async () => (await api.get(`/courses/${courseId}/materials`)).data,
  });

  const uploadMut = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", uploadForm.title || file.name);
      fd.append("kind", uploadForm.kind);
      fd.append("description", uploadForm.description || "");
      return (await api.post(`/courses/${courseId}/materials`, fd)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["course-materials", courseId] });
      toast.success("Materiale caricato");
      setUploadOpen(false);
      setUploadForm({ title: "", kind: "slide", description: "" });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore upload"),
  });

  const linkMut = useMutation({
    mutationFn: async () => (await api.post(`/courses/${courseId}/materials/link`, linkForm)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["course-materials", courseId] });
      toast.success("Link aggiunto");
      setLinkOpen(false);
      setLinkForm({ title: "", url: "", description: "" });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/courses/materials/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["course-materials", courseId] });
      toast.success("Materiale eliminato");
      setDelMaterial(null);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  return (
    <Card className="border border-stone-200 rounded-xl shadow-none">
      <div className="p-5 border-b border-stone-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium flex items-center gap-2"><FolderOpen size={14}/> Materiale didattico</div>
          <div className="text-xs text-stone-500 mt-0.5">
            {materials.length} elementi · slide, PDF, video, link consultabili dagli studenti iscritti
          </div>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setLinkOpen(true)}
              data-testid="material-add-link-btn"
              className="rounded-full"
            >
              <LinkIcon size={13} className="mr-1.5"/> Aggiungi link
            </Button>
            <Button
              onClick={() => setUploadOpen(true)}
              data-testid="material-upload-btn"
              className="rounded-full bg-[#E2725B] hover:bg-[#c85d47]"
            >
              <Upload size={13} className="mr-1.5"/> Carica file
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="p-8 text-sm text-stone-400 text-center">Caricamento…</div>
      ) : materials.length === 0 ? (
        <div className="p-10 text-center text-sm text-stone-400">
          Nessun materiale caricato ancora.
          {canManage && <div className="mt-2">Usa &laquo;Carica file&raquo; o &laquo;Aggiungi link&raquo; per iniziare.</div>}
        </div>
      ) : (
        <ul className="divide-y divide-stone-100">
          {materials.map((m) => {
            const Icon = KIND_ICON[m.kind] || FileText;
            const isLink = m.kind === "link" || m.url;
            const href = isLink ? m.url : `${API_BASE}/courses/materials/${m.id}/download?auth=${token}`;
            return (
              <li key={m.id} data-testid={`material-${m.id}`} className="p-4 flex items-center gap-3 hover:bg-stone-50/60 transition-colors">
                <div className="w-9 h-9 rounded-lg bg-stone-100 grid place-items-center text-[#1C352D] shrink-0">
                  <Icon size={16}/>
                </div>
                <div className="min-w-0 flex-1">
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-sm hover:text-[#1C352D] truncate block"
                  >
                    {m.title}
                  </a>
                  <div className="flex items-center gap-2 text-[11px] text-stone-500 mt-0.5 flex-wrap">
                    <Badge className="bg-stone-100 text-stone-700 text-[9px] rounded-full">{KIND_LABEL[m.kind] || m.kind}</Badge>
                    {m.uploaded_by_name && <span>Caricato da {m.uploaded_by_name}</span>}
                    <span>· {fmtDate(m.uploaded_at)}</span>
                    {m.size > 0 && <span>· {(m.size / 1024 / 1024).toFixed(1)} MB</span>}
                  </div>
                  {m.description && <div className="text-xs text-stone-600 mt-1">{m.description}</div>}
                </div>
                <a
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="p-2 rounded hover:bg-stone-100 text-stone-500 hover:text-[#1C352D]"
                  data-testid={`material-download-${m.id}`}
                >
                  {isLink ? <ExternalLink size={15}/> : <Download size={15}/>}
                </a>
                {canManage && (
                  <button
                    onClick={() => setDelMaterial(m)}
                    className="p-2 rounded hover:bg-red-50 text-stone-500 hover:text-red-700"
                    data-testid={`material-delete-${m.id}`}
                  >
                    <Trash2 size={15}/>
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Upload file dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Carica materiale</DialogTitle>
            <DialogDescription>Slide, PDF, documenti o video (max 100MB).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Titolo</Label>
              <Input
                data-testid="material-title-input"
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                placeholder="Es. Slide lezione 1"
              />
            </div>
            <div>
              <Label className="text-xs">Tipo</Label>
              <Select value={uploadForm.kind} onValueChange={(v) => setUploadForm({ ...uploadForm, kind: v })}>
                <SelectTrigger data-testid="material-kind-select"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="slide">Slide</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Descrizione (facoltativa)</Label>
              <Textarea
                rows={2}
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              />
            </div>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && uploadMut.mutate(e.target.files[0])}
              data-testid="material-file-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Annulla</Button>
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploadMut.isPending || !uploadForm.title}
              data-testid="material-upload-submit"
              className="bg-[#1C352D] hover:bg-[#264A3E]"
            >
              {uploadMut.isPending ? "Caricamento…" : "Seleziona file"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add link dialog */}
      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Aggiungi link</DialogTitle>
            <DialogDescription>Video YouTube, articoli, risorse esterne.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Titolo</Label>
              <Input
                data-testid="link-title-input"
                value={linkForm.title}
                onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">URL</Label>
              <Input
                data-testid="link-url-input"
                value={linkForm.url}
                onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })}
                placeholder="https://…"
              />
            </div>
            <div>
              <Label className="text-xs">Descrizione (facoltativa)</Label>
              <Textarea
                rows={2}
                value={linkForm.description}
                onChange={(e) => setLinkForm({ ...linkForm, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)}>Annulla</Button>
            <Button
              onClick={() => linkMut.mutate()}
              disabled={linkMut.isPending || !linkForm.title || !linkForm.url}
              data-testid="link-submit"
              className="bg-[#1C352D] hover:bg-[#264A3E]"
            >
              {linkMut.isPending ? "Salvataggio…" : "Aggiungi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delMaterial} onOpenChange={(v) => !v && setDelMaterial(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare &laquo;{delMaterial?.title}&raquo;?</AlertDialogTitle>
            <AlertDialogDescription>L&apos;operazione non è reversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="material-del-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => delMaterial && delMut.mutate(delMaterial.id)}
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
